import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OptionsButton from 'tripPlanner/assistant/components/HistoryItemOptions/OptionsButton'
import AssistantHistoryItemMenuActions from './AssistantHistoryItemMenuActions'
import useOpenRecentChatItemActionsSheet from './useOpenRecentChatItemActionsSheet'

const ACTIONS_ICON = <LineEllipsisVIcon />

interface Props {
  title: string
  onBeginRename: (closeMenu: () => void) => void
  onRename: (name: string) => Promise<void>
  onDelete: () => void
  onDownloadPdf?: () => void
  onDownloadSpreadsheet?: () => void
}

/** Opens an actions sheet on mobile and a dropdown menu elsewhere. */
function AssistantHistoryItemActions({
  title,
  onBeginRename,
  onRename,
  onDelete,
  onDownloadPdf,
  onDownloadSpreadsheet,
}: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const actionsTitle = intl.formatMessage({
    id: '-2Bx0_gh3waeLxWso_GQ',
    defaultMessage: 'Chat actions',
    description: 'Accessible title for the actions menu button on a chat row in the TripPlanner side navigation, the menu drawer and the offer-page chat history',
  })
  const actionMenuAriaLabel = intl.formatMessage({
    id: 'OSwxcyWZf8-szg3KOL5Q',
    defaultMessage: 'action menu',
    description: 'Accessible label for the document options menu button',
  })

  const openActionsSheet = useOpenRecentChatItemActionsSheet({
    title,
    onRename,
    onDelete,
    onDownloadPdf,
    onDownloadSpreadsheet,
  })

  const renderMenuActions = useCallback((closeMenu: () => void) => (
    <AssistantHistoryItemMenuActions
      closeMenu={closeMenu}
      onBeginRename={onBeginRename}
      onDelete={onDelete}
      onDownloadPdf={onDownloadPdf}
      onDownloadSpreadsheet={onDownloadSpreadsheet}
    />
  ), [onBeginRename, onDelete, onDownloadPdf, onDownloadSpreadsheet])

  return (
    <>
      {isMobile && (
        <IconButton
          kind="tertiary"
          shape="circle"
          size="small"
          data-testid="action-menu-button"
          aria-label={actionMenuAriaLabel}
          title={actionsTitle}
          onClick={openActionsSheet}
        >
          {ACTIONS_ICON}
        </IconButton>
      )}
      {!isMobile && (
        <OptionsButton
          title={actionsTitle}
          bodyContent={renderMenuActions}
        />
      )}
    </>
  )
}

export default AssistantHistoryItemActions
